/**
 * British Columbia incident documentation.
 * Residential Care Regulation (B.C. Reg. 96/2009) and Assisted Living Regulation (B.C. Reg. 189/2019).
 * NeuraVue never submits a form. Reportability follows the nurse's answers, not the camera.
 * Browser copy of app/incidents/bc.js. Change the rules there, then copy this file.
 */

const PRODUCT_BEHAVIOUR = "Responsive Behaviour / Abuse";
const DEFAULT_GRACE_MINUTES = 60;
const LATE_MS = 30 * 60 * 1000;

const PROVINCES = ["ON", "QC", "BC", "other"];
const LICENCE_TYPES = ["LTC", "AL"];
const HEALTH_AUTHORITIES = ["FHA", "VCH", "IHA", "NHA", "VIHA", "PHC"];
const BC_DESIGNATIONS = ["RN", "LPN", "RPN", "NP", "HCA"];
const CONDUCTS = ["force", "verbal", "sexual", "unmet_need", "financial", "none"];
const OTHER_PARTIES = ["resident", "staff", "visitor", "unknown", "no_one"];

const CATEGORIES = {
  fall: { regulatory: "Fall", product: "Fall" },
  physical_abuse: { regulatory: "Physical abuse", product: PRODUCT_BEHAVIOUR },
  emotional_abuse: { regulatory: "Emotional abuse", product: PRODUCT_BEHAVIOUR },
  sexual_abuse: { regulatory: "Sexual abuse", product: PRODUCT_BEHAVIOUR },
  financial_abuse: { regulatory: "Financial abuse", product: PRODUCT_BEHAVIOUR },
  neglect: { regulatory: "Neglect", product: PRODUCT_BEHAVIOUR },
  aggression_between_persons_in_care: {
    regulatory: "Aggression between persons in care",
    product: PRODUCT_BEHAVIOUR
  },
  aggressive_or_unusual_behaviour: {
    regulatory: "Aggressive or unusual behaviour",
    product: PRODUCT_BEHAVIOUR
  },
  emergency_restraint: { regulatory: "Emergency restraint", product: PRODUCT_BEHAVIOUR },
  not_reportable: { regulatory: "Not reportable; facility record", product: PRODUCT_BEHAVIOUR }
};

const CONDUCT_CATEGORY = {
  force: "physical_abuse",
  verbal: "emotional_abuse",
  sexual: "sexual_abuse",
  unmet_need: "neglect",
  financial: "financial_abuse"
};

const NON_RESIDENT = new Set(["physical_abuse", "emotional_abuse", "sexual_abuse", "financial_abuse", "neglect"]);

function incidentDocsBcEnabled(env = process.env) {
  return env.INCIDENT_DOCS_BC === "true";
}

function allowedProvinces(env = process.env) {
  return incidentDocsBcEnabled(env) ? ["ON", "QC", "BC", "other"] : ["ON", "QC", "other"];
}

function provinceErrorMessage(env = process.env) {
  return incidentDocsBcEnabled(env)
    ? "Province must be ON, QC, BC, or other."
    : "Province must be ON, QC, or other.";
}

function behaviourType(type) {
  return type === "unsafe_interaction" || type === "responsive_behaviour";
}

function productLabel(type) {
  if (type === "fall") return "Fall";
  if (behaviourType(type)) return PRODUCT_BEHAVIOUR;
  if (type === "wandering_zone") return "Wandering or exit-seeking";
  return "Other alert";
}

function addMs(iso, ms) {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return new Date(at.getTime() + ms).toISOString();
}

function normalizeGrace(value) {
  if (value == null || value === "") return DEFAULT_GRACE_MINUTES;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0 || n > 1440) return null;
  return n;
}

function jurisdictionFromSite(site, env = process.env) {
  const raw = site?.incidentJurisdiction || {};
  const namesAllowed = raw.residentNamesAllowed !== false;
  let province = PROVINCES.includes(raw.province) ? raw.province : "other";
  if (province === "BC" && !incidentDocsBcEnabled(env)) province = "other";
  const bc = province === "BC";
  const licence = LICENCE_TYPES.includes(raw.bcLicenceType) ? raw.bcLicenceType : null;
  const grace = normalizeGrace(raw.overdueGraceMinutes);
  return {
    province,
    bcLicenceType: bc ? licence : null,
    healthAuthority: bc && HEALTH_AUTHORITIES.includes(raw.healthAuthority) ? raw.healthAuthority : null,
    licenceNumber: String(raw.licenceNumber || "").trim(),
    funded: raw.funded === true,
    overdueGraceMinutes: grace == null ? DEFAULT_GRACE_MINUTES : grace,
    postFallProtocol: {
      name: String(raw.postFallProtocol?.name || "").trim(),
      version: String(raw.postFallProtocol?.version || "").trim()
    },
    agaAgencyPhone: String(raw.agaAgencyPhone || "").trim(),
    aiDocPolicyAck: raw.aiDocPolicyAck === true,
    residentNamesAllowed: namesAllowed
  };
}

function bcTemplateFromJurisdiction(jurisdiction) {
  if (!jurisdiction || jurisdiction.province !== "BC") return null;
  if (jurisdiction.bcLicenceType === "AL") return "BC_AL";
  if (jurisdiction.bcLicenceType === "LTC") return "BC_LTC";
  return null;
}

function bcTemplate(incident) {
  if (incident?.province !== "BC") return null;
  if (incident.bc_template === "BC_AL" || incident.bc_template === "BC_LTC") return incident.bc_template;
  return bcTemplateFromJurisdiction(incident.site_jurisdiction);
}

function normalizeJurisdiction(input = {}, env = process.env) {
  const errors = [];
  const source = input && typeof input === "object" ? input : {};
  const province = source.province || "other";
  if (!PROVINCES.includes(province)) errors.push("Province must be ON, QC, BC, or other.");
  if (province === "BC" && !incidentDocsBcEnabled(env)) {
    errors.push("British Columbia incident documentation is not enabled.");
  }
  let bcLicenceType = source.bcLicenceType || source.careSetting || null;
  if (bcLicenceType === "") bcLicenceType = null;
  if (bcLicenceType && !LICENCE_TYPES.includes(bcLicenceType)) {
    errors.push("Care setting must be long-term care (LTC) or assisted living (AL).");
  }
  if (province === "BC" && !bcLicenceType) {
    errors.push("A British Columbia home needs a care setting: long-term care (LTC) or assisted living (AL).");
  }
  if (province !== "BC") bcLicenceType = null;

  let healthAuthority = source.healthAuthority || null;
  if (healthAuthority === "") healthAuthority = null;
  if (healthAuthority && !HEALTH_AUTHORITIES.includes(healthAuthority)) {
    errors.push("Health authority must be FHA, IHA, VIHA, NHA, VCH, or PHC.");
  }
  if (province === "BC" && bcLicenceType === "LTC" && !healthAuthority) {
    errors.push("A long-term care home needs its health authority so staff know where to file.");
  }
  if (province !== "BC") healthAuthority = null;

  const grace = normalizeGrace(source.overdueGraceMinutes);
  if (grace == null) {
    errors.push("The site-policy grace before an immediate duty shows as overdue must be from 0 to 1440 minutes.");
  }

  const protocol = source.postFallProtocol || {};
  const value = {
    province,
    bcLicenceType,
    healthAuthority,
    licenceNumber: String(source.licenceNumber || "").trim(),
    funded: source.funded === true,
    overdueGraceMinutes: grace == null ? DEFAULT_GRACE_MINUTES : grace,
    postFallProtocol: {
      name: String(protocol.name || "").trim(),
      version: String(protocol.version || "").trim()
    },
    agaAgencyPhone: String(source.agaAgencyPhone || "").trim(),
    aiDocPolicyAck: source.aiDocPolicyAck === true,
    residentNamesAllowed: source.residentNamesAllowed !== false
  };
  return { ok: errors.length === 0, errors, value };
}

function text(value) {
  return String(value == null ? "" : value).trim();
}

function filled(value) {
  return text(value).length > 0;
}

function nameValuePresent(value) {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "object" && typeof value.value === "string") return value.value.trim().length > 0;
  return false;
}

function residentNameError(incident, save) {
  if (incident?.site_jurisdiction?.residentNamesAllowed !== false) return null;
  const doc = save?.documentation || {};
  const candidates = [
    doc.resident_name,
    doc.residentName,
    doc.other_resident_name,
    doc.otherResidentName,
    doc.resident?.display_name,
    save?.resident_name,
    save?.resident?.display_name,
    save?.otherResidentName
  ];
  if (candidates.some(nameValuePresent)) {
    return "This home does not store resident names. Enter the room and the resident identifier only.";
  }
  return null;
}

const REPORTER_KEYS = [
  "aga_reporter",
  "aga_reporter_name",
  "agaReporterName",
  "reporter_name",
  "reporterName",
  "aga_reporter_user_id"
];

function stripReporterIdentity(doc) {
  if (!doc || typeof doc !== "object") return doc;
  const next = { ...doc };
  REPORTER_KEYS.forEach((key) => delete next[key]);
  if (next.aga && typeof next.aga === "object") {
    const aga = { ...next.aga };
    delete aga.reporter;
    delete aga.reporter_name;
    delete aga.name;
    delete aga.user_id;
    next.aga = aga;
  }
  return next;
}

function residentInjured(doc = {}) {
  if (doc.hospital_transfer === "yes" || doc.emergency_care === "yes" || doc.first_aid_needed === "yes") return true;
  if (doc.head_strike === "yes") return true;
  if (doc.injuries_none === true) return false;
  return filled(doc.injuries);
}

function injuryThreshold(doc = {}) {
  return doc.first_aid_needed === "yes" || doc.emergency_care === "yes" || doc.hospital_transfer === "yes";
}

function categoryResult(category, reportable, extra = {}) {
  const meta = CATEGORIES[category] || CATEGORIES.not_reportable;
  return {
    category: extra.needs_nurse_choice ? null : category,
    regulatory_term: extra.needs_nurse_choice ? "Choose the regulatory category" : meta.regulatory,
    product_label: extra.product || meta.product,
    reportable,
    additional: extra.additional || [],
    mapping: extra.mapping || "",
    needs_nurse_choice: Boolean(extra.needs_nurse_choice)
  };
}

function bcCategorySuggest(incident, doc = {}) {
  const type = incident?.type;
  const additional = doc.restraint_used === "yes" ? ["emergency_restraint"] : [];
  if (type === "fall") {
    const reportable = doc.emergency_care === "yes" || doc.hospital_transfer === "yes";
    return categoryResult(reportable ? "fall" : "not_reportable", reportable, {
      product: "Fall",
      additional,
      mapping: reportable
        ? "Schedule D: a fall is reportable when it needs emergency care by a physician or nurse practitioner, or transfer to a hospital."
        : "A fall without that care is not reportable. Keep a facility record. Tell the contact person if the resident was injured (s.76)."
    });
  }
  if (!behaviourType(type)) {
    return categoryResult("not_reportable", additional.length > 0, {
      product: productLabel(type),
      additional,
      mapping: "This alert has no Schedule D category in the British Columbia template."
    });
  }

  const other = doc.other_party_kind;
  if (!OTHER_PARTIES.includes(other)) {
    return categoryResult("not_reportable", false, {
      additional,
      needs_nurse_choice: true,
      mapping: "Say who the other person was. The camera does not decide the category."
    });
  }

  if (other === "resident") {
    if (injuryThreshold(doc)) {
      return categoryResult("aggression_between_persons_in_care", true, {
        additional,
        mapping:
          "The other person is a person in care, and the injury needed first aid, emergency physician or nurse practitioner care, or transfer to hospital. This is not an abuse category. Abuse categories are for a person who is not in care."
      });
    }
    if (doc.in_care_plan === "yes") {
      const reportable = additional.includes("emergency_restraint");
      return categoryResult(reportable ? "emergency_restraint" : "not_reportable", reportable, {
        additional: reportable ? [] : additional,
        mapping:
          "The behaviour is already in the care plan and the injury threshold was not met. Keep a facility record unless an emergency restraint was used."
      });
    }
    if (doc.in_care_plan === "no") {
      return categoryResult("aggressive_or_unusual_behaviour", true, {
        additional,
        mapping:
          "A person in care acted toward another person, the behaviour is not in the care plan, and it is not aggression between persons in care."
      });
    }
    return categoryResult("not_reportable", additional.includes("emergency_restraint"), {
      additional,
      needs_nurse_choice: true,
      mapping: "Say whether this behaviour is already in the care plan."
    });
  }

  if (other === "no_one") {
    const reportable = additional.includes("emergency_restraint");
    return categoryResult(reportable ? "emergency_restraint" : "not_reportable", reportable, {
      additional: reportable ? [] : additional,
      mapping: "No other person was involved."
    });
  }

  const suspected = doc.suspected_abuse;
  const mapped = CONDUCT_CATEGORY[doc.conduct];
  if (suspected === "no" && !mapped) {
    const reportable = additional.includes("emergency_restraint");
    return categoryResult(reportable ? "emergency_restraint" : "not_reportable", reportable, {
      additional,
      mapping:
        "The other person is not a person in care, and abuse or neglect is not suspected. Keep a facility record unless an emergency restraint was used."
    });
  }
  if (mapped && suspected !== "no") {
    return categoryResult(mapped, true, {
      additional,
      mapping: conductMapping(doc.conduct, other)
    });
  }
  if (mapped && suspected === "no") {
    return categoryResult(mapped, true, {
      additional,
      mapping: `${conductMapping(doc.conduct, other)} You also answered that abuse or neglect is not suspected. Talk to the manager before you confirm the category.`
    });
  }
  if (suspected === "yes" || suspected === "unsure") {
    return categoryResult("not_reportable", true, {
      additional,
      needs_nurse_choice: true,
      mapping:
        suspected === "unsure"
          ? "Unsure is reportable. The duty covers alleged or suspected abuse or neglect. Talk to the manager and choose the category."
          : "Choose the category that matches what you saw. The other person is not a person in care, so the abuse and neglect categories apply, not aggression between persons in care."
    });
  }
  return categoryResult("not_reportable", additional.includes("emergency_restraint"), {
    additional,
    needs_nurse_choice: true,
    mapping: "Say whether you suspect abuse or neglect."
  });
}

function conductMapping(conduct, other) {
  const who =
    other === "staff" ? "A staff member" : other === "visitor" ? "A visitor" : "A person who is not confirmed as a person in care";
  if (conduct === "force") return `${who} used force. Regulatory category: physical abuse. Product label: ${PRODUCT_BEHAVIOUR}.`;
  if (conduct === "verbal") {
    return `${who}: yelling, harassment, or confinement. Regulatory category: emotional abuse. Product label: ${PRODUCT_BEHAVIOUR}.`;
  }
  if (conduct === "sexual") {
    return `${who}: sexual behaviour toward the resident. Regulatory category: sexual abuse. Consenting sexual behaviour between adult persons in care is not this category. Product label: ${PRODUCT_BEHAVIOUR}.`;
  }
  if (conduct === "unmet_need") return `A care need was not met by a care provider. Regulatory category: neglect. Product label: ${PRODUCT_BEHAVIOUR}.`;
  if (conduct === "financial") return `${who}: money or property. Regulatory category: financial abuse. Product label: ${PRODUCT_BEHAVIOUR}.`;
  return "";
}

function bcReportable(incident, doc = {}) {
  const suggestion = bcCategorySuggest(incident, doc);
  const template = bcTemplate(incident);
  return {
    reportable: Boolean(template) && suggestion.reportable,
    category: suggestion.category,
    regulatory_term: suggestion.regulatory_term,
    facility_record: Boolean(template),
    family_notice: Boolean(template) && (suggestion.reportable || residentInjured(doc)),
    suggestion
  };
}

function categoryFits(incident, doc, chosen) {
  if (!chosen || !CATEGORIES[chosen]) return false;
  const suggestion = bcCategorySuggest(incident, doc);
  if (incident.type === "fall") {
    return suggestion.reportable ? chosen === "fall" : chosen === "not_reportable";
  }
  if (!behaviourType(incident.type)) return chosen === "not_reportable" || chosen === "emergency_restraint";
  const other = doc.other_party_kind;
  if (other === "resident") {
    if (injuryThreshold(doc)) return chosen === "aggression_between_persons_in_care";
    if (doc.in_care_plan === "no") return chosen === "aggressive_or_unusual_behaviour";
    if (doc.in_care_plan === "yes") {
      return doc.restraint_used === "yes" ? chosen === "emergency_restraint" : chosen === "not_reportable";
    }
    return false;
  }
  if (other === "no_one") {
    return doc.restraint_used === "yes" ? chosen === "emergency_restraint" : chosen === "not_reportable";
  }
  if (other === "staff" || other === "visitor" || other === "unknown") {
    const abusePath =
      doc.suspected_abuse === "yes" || doc.suspected_abuse === "unsure" || Boolean(CONDUCT_CATEGORY[doc.conduct]);
    if (!abusePath) {
      return doc.restraint_used === "yes" ? chosen === "emergency_restraint" : chosen === "not_reportable";
    }
    if (chosen === "not_reportable" || chosen === "emergency_restraint") return false;
    return NON_RESIDENT.has(chosen);
  }
  return false;
}

function filingCard(incident) {
  const template = bcTemplate(incident);
  const authority = incident?.site_jurisdiction?.healthAuthority || null;
  const channels = {
    FHA: {
      name: "Fraser Health",
      channel:
        "Fraser Health online incident reporting system for licensed care facilities. You submit it there. NeuraVue does not."
    },
    IHA: {
      name: "Interior Health",
      channel:
        "Interior Health Licensing Direct. Include who, what, when, where, how, and why, the lead-up, what you did, who you told, and care-plan changes. A manager reviews and signs. Confirm the residential form with Interior Health. The public child-care page is not this home's form."
    },
    VIHA: {
      name: "Island Health",
      channel:
        "Island Health Incident Reporting Portal, using this facility's PIN account. You submit it there. NeuraVue does not."
    },
    NHA: {
      name: "Northern Health",
      channel:
        "Tell the medical health officer by phone, email, or fax, and send the reportable-incident PDF to licensingconnect@northernhealth.ca. You send it. NeuraVue does not."
    },
    VCH: {
      name: "Vancouver Coastal Health",
      channel:
        "Vancouver Coastal Health uses its own reportable-incident form. The form fields were not verified. You submit it. NeuraVue does not."
    },
    PHC: {
      name: "Providence Health Care",
      channel: "Confirm the Providence Health Care licensing channel for this home. NeuraVue does not submit it."
    }
  };
  const card = {
    template,
    submitted_by_neuravue: false,
    health_authority: authority && channels[authority] ? channels[authority] : null,
    assisted_living_registry:
      template === "BC_AL"
        ? {
            form: "HLTH 1622",
            email: "Hlth.assistedlivingregistry@gov.bc.ca",
            fax: "250-953-0496",
            channel:
              "Within 24 hours of the incident, send HLTH 1622 to the Assisted Living Registry. You send the email or fax. NeuraVue does not."
          }
        : null,
    unverified: ["Vancouver Coastal Health form", "HLTH 3409 fields", "PSLS field definitions"]
  };
  return card;
}

function bcDeadlines(incident, doc = incident?.documentation || {}) {
  if (!incident?.confirmed_at || incident.province !== "BC") return [];
  const template = bcTemplate(incident);
  if (!template) return [];
  const started = incident.confirmed_at;
  const grace = normalizeGrace(incident.site_jurisdiction?.overdueGraceMinutes);
  const graceMinutes = grace == null ? DEFAULT_GRACE_MINUTES : grace;
  const overdueAt = addMs(started, graceMinutes * 60 * 1000);
  const suggestion = bcCategorySuggest(incident, doc);
  const reportable = suggestion.reportable;
  const funded = incident.site_jurisdiction?.funded === true;
  const injured = residentInjured(doc);
  const crime = doc.criminal_offence === "yes" || doc.possible_crime === "yes";
  const rows = [];

  function immediate(party, role, required, basis) {
    if (!required) return;
    rows.push({
      party,
      role,
      required: true,
      advisory: false,
      legal: false,
      site_policy: true,
      dueAt: overdueAt,
      grace_minutes: graceMinutes,
      display: "due_now",
      basis: `${basis} Due now. If this is still open after ${graceMinutes} minutes, it is overdue under site policy. The regulation says immediately and does not set this number.`,
      started_at: started
    });
  }

  immediate(
    "charge_nurse",
    "Nurse in charge",
    true,
    "Tell the nurse in charge. This is a home process step, not a numbered statutory deadline."
  );

  const manager = reportable || doc.suspected_abuse === "yes" || doc.suspected_abuse === "unsure";
  immediate(
    "doc",
    "Manager or director of care",
    manager,
    "Tell the manager or director of care when the incident is reportable, or when abuse or neglect is suspected or unsure."
  );

  if (template === "BC_LTC") {
    const family = reportable || injured;
    immediate(
      "sdm_family",
      "Parent, representative, or contact person",
      family,
      family && !reportable
        ? "Residential Care Regulation s.76: tell the contact person about an illness or injury even when the incident is not reportable."
        : "Residential Care Regulation s.77(2)(a) and, for any illness or injury, s.76: tell the contact person."
    );
    const physician =
      reportable || (incident.type === "fall" && (doc.head_strike === "yes" || doc.head_strike === "unknown" || injured));
    immediate(
      "physician",
      "Medical practitioner or nurse practitioner",
      physician,
      reportable
        ? "Residential Care Regulation s.77(2)(b): tell the medical practitioner or nurse practitioner."
        : "Home fall policy. The regulation requires a fall-response policy (s.85(2)(a)) and does not set a neuro-check interval."
    );
    immediate(
      "bc_mho",
      "Medical health officer, through health authority licensing",
      reportable,
      "Residential Care Regulation s.77(2)(c): tell a medical health officer, in the form and manner the health authority requires. Submit the health authority form yourself. NeuraVue does not."
    );
    immediate(
      "bc_funding",
      "Funding program",
      reportable && funded,
      "Residential Care Regulation s.77(2)(d): tell the funding program."
    );
  }

  if (template === "BC_AL") {
    const eventTime = doc.event_time || incident.alert_at;
    const legalDue = addMs(eventTime, 24 * 60 * 60 * 1000);
    const fromEvent = Boolean(doc.event_time);
    function legal(party, role, required, basis) {
      if (!required) return;
      rows.push({
        party,
        role,
        required: true,
        advisory: false,
        legal: true,
        site_policy: false,
        dueAt: legalDue,
        legal_due_at: legalDue,
        event_time: eventTime,
        event_time_source: fromEvent ? "event_time" : "alert_time",
        display: "legal_24h",
        basis: `${basis} The legal deadline is 24 hours after the event time, including weekends and holidays. Confirmation time is shown beside it and is not the legal deadline. NeuraVue does not send the form.`,
        started_at: started
      });
    }
    const alReportable = reportable || doc.suspected_abuse === "yes" || doc.suspected_abuse === "unsure";
    legal(
      "bc_alr",
      "Assisted Living Registry (HLTH 1622)",
      alReportable,
      "Assisted Living Regulation s.51: report to the registrar on HLTH 1622."
    );
    legal(
      "sdm_family",
      "Contact person, and anyone the resident asks",
      alReportable,
      "Assisted Living Regulation s.51: tell the contact person if appropriate, and anyone the resident asks."
    );
    legal(
      "bc_funding",
      "Funding program or health authority case manager",
      alReportable && funded,
      "Assisted Living Regulation s.51: tell the funding program representative."
    );
    if (injured) {
      rows.push({
        party: "physician",
        role: "Physician or nurse practitioner",
        required: false,
        advisory: true,
        legal: false,
        site_policy: false,
        dueAt: null,
        display: "advisory",
        basis:
          "Not required by Assisted Living Regulation s.51. Shown because the resident was injured. This is not a legal deadline.",
        started_at: started
      });
    }
  }

  immediate("police", "Police", crime, "Tell the police when a crime is possible, and record the occurrence number.");

  const aga =
    behaviourType(incident.type) &&
    doc.other_party_kind &&
    doc.other_party_kind !== "resident" &&
    doc.other_party_kind !== "no_one" &&
    (doc.suspected_abuse === "yes" || doc.suspected_abuse === "unsure" || NON_RESIDENT.has(suggestion.category));
  if (aga) {
    rows.push({
      party: "bc_aga",
      role: "Designated agency (Adult Guardianship Act)",
      required: false,
      advisory: true,
      legal: false,
      site_policy: false,
      dueAt: null,
      display: "advisory",
      basis:
        "Adult Guardianship Act s.46: a report is optional when an adult may be abused or neglected and unable to seek help. The mandatory duty is the licence regulation. Do not record the reporter's name.",
      started_at: started,
      phone: incident.site_jurisdiction?.agaAgencyPhone || ""
    });
  }

  return rows;
}

function notificationComplete(incident, party) {
  return (incident.notifications || []).some((row) => {
    if (row.party !== party || row.done !== true || !row.at) return false;
    if (!filled(row.person_name)) return false;
    if (!filled(row.person_title || row.title)) return false;
    if (!filled(row.note || row.response)) return false;
    if (!filled(row.method)) return false;
    return true;
  });
}

function isLate(doc, now) {
  const eventIso = doc.event_time || doc.discovered_at;
  if (!eventIso) return false;
  const eventAt = new Date(eventIso);
  if (Number.isNaN(eventAt.getTime())) return false;
  return now.getTime() - eventAt.getTime() > LATE_MS;
}

function requireText(errors, value, message) {
  if (!filled(value)) errors.push(message);
}

function validateBc(incident, now = new Date(), actor = null) {
  const errors = [];
  const doc = incident.documentation || {};
  const template = bcTemplate(incident);
  if (!incident.confirmed_at) {
    errors.push("A staff member must confirm the alert before this record can be finalized.");
    return errors;
  }
  if (!template) {
    errors.push("This home has no British Columbia care setting. A facility admin sets long-term care or assisted living.");
    return errors;
  }
  if (incident.site_jurisdiction?.aiDocPolicyAck !== true) {
    errors.push("This home has not confirmed an employer policy for AI-assisted documentation. A facility admin records that before a British Columbia record is finalized. The nurse remains accountable.");
  }
  if (!incident.province_confirmed) {
    errors.push("Confirm the home's province. You cannot replace it on this record.");
  }
  const namesOff = incident.site_jurisdiction?.residentNamesAllowed === false;
  if (namesOff && (filled(doc.resident_name) || nameValuePresent(doc.residentName) || filled(doc.other_resident_name))) {
    errors.push("This home does not store resident names. Enter the room and the resident identifier only.");
  }
  const identifier = filled(doc.resident_room) || filled(incident.room) || filled(doc.pcc_patient_id) || (!namesOff && filled(doc.resident_name));
  if (!identifier) errors.push("Record the room or the resident identifier.");
  if (!doc.event_time_confirmed || !doc.event_time) errors.push("Confirm the event time.");
  if (!doc.discovered_at_confirmed || !doc.discovered_at) errors.push("Record when staff discovered the event.");
  if (!doc.location_confirmed || !filled(doc.location || incident.location)) errors.push("Confirm the location.");
  if (!doc.witnessed) errors.push("Record whether staff witnessed the event.");
  if (doc.witnessed === "yes") {
    requireText(errors, doc.witnessed_by, "Record the name of the staff who witnessed it.");
    requireText(errors, doc.witnessed_title, "Record the title of the staff who witnessed it.");
  }
  requireText(errors, doc.observed, "Write what you saw and heard, in your own words.");
  requireText(errors, doc.resident_statement, "Record the resident's own account, or that they could not say.");
  requireText(errors, doc.persons_present, "Record who was present or who discovered it.");
  requireText(errors, doc.leading_events, "Record what led up to the event.");
  requireText(errors, doc.care_given, "Record the immediate care given and who gave it.");
  if (!doc.hospital_transfer) errors.push("Record whether the resident was transferred to hospital.");
  if (doc.hospital_transfer === "yes") {
    requireText(errors, doc.hospital_name, "Record where the resident was transferred, and when.");
    if (!doc.hospital_time) errors.push("Record the time of the hospital transfer.");
  }
  if (doc.emergency_care !== "yes" && doc.emergency_care !== "no") {
    errors.push("Record whether a physician or nurse practitioner gave emergency care.");
  }
  if (doc.injuries_none !== true) requireText(errors, doc.injuries, "Describe injuries, or choose none observed.");
  requireText(errors, doc.consciousness, "Record level of consciousness.");
  if (!filled(doc.vitals) && !filled(doc.vitals_not_done_reason)) {
    errors.push("Record vital signs and the time, or why they were not taken.");
  }
  requireText(errors, doc.pain, "Record pain, or why it was not scored.");
  requireText(errors, doc.corrective_actions, "Record corrective actions and care-plan changes.");
  requireText(errors, doc.outcome, "Record how the resident is now.");
  if (isLate(doc, now) && !filled(doc.late_entry_reason)) {
    errors.push("This entry is late. Record the entry time, the event time, and the reason.");
  }
  if (!doc.attestation_accepted) errors.push("Attest that you reviewed this record and it is accurate.");
  requireText(errors, doc.attestation_name, "Sign with your full name.");
  const designation = text(doc.attestation_designation).toUpperCase();
  if (!BC_DESIGNATIONS.includes(designation)) {
    errors.push("Enter your designation: RN, LPN, RPN (registered psychiatric nurse), NP, or HCA.");
  }
  if (actor) {
    if (filled(doc.attestation_name) && text(doc.attestation_name) !== text(actor.name)) {
      errors.push("Sign your own entry. Your name comes from your account.");
    }
    if (doc.attestation_user_id && String(doc.attestation_user_id) !== String(actor.user_id)) {
      errors.push("Sign your own entry. A nurse documents the care they personally gave.");
    }
    (incident.notifications || []).forEach((row) => {
      if (row.done && row.recorded_by && String(row.recorded_by) !== String(actor.user_id)) {
        errors.push("A notification was recorded by someone else. You can only sign entries you made.");
      }
    });
  }

  if (incident.type === "fall") {
    requireText(errors, doc.contributing_factors, "Record contributing factors, or none identified.");
    if (!["yes", "no", "unknown"].includes(doc.head_strike)) {
      errors.push("Record whether the resident's head was struck. If nobody saw the fall, choose unknown.");
    }
    if (doc.head_strike === "yes" || doc.head_strike === "unknown") {
      if (doc.neuro_checks_started !== "yes" && doc.neuro_checks_started !== "no") {
        errors.push("Record whether neuro checks were started.");
      }
      const protocol = incident.site_jurisdiction?.postFallProtocol || {};
      const siteName = text(protocol.name);
      const siteVersion = text(protocol.version);
      if (!siteName || !siteVersion) {
        errors.push("This home has no post-fall schedule on file. A facility admin enters the name and version from the home's policy. Do not invent an interval.");
      } else if (doc.neuro_checks_started === "yes") {
        if (text(doc.post_fall_protocol_name) !== siteName || text(doc.post_fall_protocol_version) !== siteVersion) {
          errors.push("Use the post-fall schedule on file for this home. Do not enter a different interval.");
        }
      } else if (!filled(doc.neuro_checks_reason)) {
        errors.push("Record why neuro checks were not started.");
      }
    }
    if (doc.environment_checked !== "yes" && doc.environment_checked !== "no") {
      errors.push("Record whether the environment was checked.");
    }
    requireText(errors, doc.environment_note, "Record what you checked in the environment, or why it was not checked.");
    requireText(errors, doc.care_plan_updated, "Record whether the fall plan and the care plan were reviewed.");
  }

  if (behaviourType(incident.type)) {
    if (!OTHER_PARTIES.includes(doc.other_party_kind)) {
      errors.push("Record who the other person was. The camera does not decide this.");
    }
    if (doc.other_party_kind === "resident") {
      requireText(errors, doc.other_resident_room, "Record the other resident's room. Do not record a name when this home does not allow names.");
      if (doc.in_care_plan !== "yes" && doc.in_care_plan !== "no") {
        errors.push("Record whether this behaviour is already in the care plan.");
      }
    }
    if (doc.other_party_kind === "staff" || doc.other_party_kind === "visitor" || doc.other_party_kind === "unknown") {
      if (!["yes", "no", "unsure"].includes(doc.suspected_abuse)) {
        errors.push("Record whether you suspect abuse or neglect.");
      }
      if (doc.suspected_abuse === "yes" || doc.suspected_abuse === "unsure") {
        if (!CONDUCTS.includes(doc.conduct) || doc.conduct === "none") {
          errors.push("Choose what you saw so the regulatory category can be suggested.");
        }
      }
    }
    if (doc.other_party_kind === "staff" && doc.staff_removed_from_care !== "yes" && doc.staff_removed_from_care !== "no") {
      errors.push("Record whether the involved staff member was removed from this resident's care.");
    }
    if (doc.first_aid_needed !== "yes" && doc.first_aid_needed !== "no") {
      errors.push("Record whether the injury needed first aid.");
    }
    requireText(errors, doc.witness_residents, "Record residents who witnessed this, or write none.");
    requireText(errors, doc.distress, "Record distress you saw, or none observed.");
    requireText(errors, doc.intervention, "Record the intervention and how the resident responded.");
    if (doc.restraint_used !== "yes" && doc.restraint_used !== "no") {
      errors.push("Record whether an emergency restraint was used.");
    }
    if (!["yes", "no", "unsure"].includes(doc.criminal_offence)) {
      errors.push("Record whether a crime is possible.");
    }
    if (doc.evidence_preserved !== "yes" && doc.evidence_preserved !== "no") {
      errors.push("Record whether the clip is being held.");
    }
    if (namesOff && filled(doc.other_resident_name)) {
      errors.push("This home does not store resident names. Use the other resident's room.");
    }
  }

  const suggestion = bcCategorySuggest(incident, doc);
  if (suggestion.reportable || (doc.bc_category && doc.bc_category !== "not_reportable")) {
    if (!doc.bc_category_confirmed || !categoryFits(incident, doc, doc.bc_category)) {
      errors.push("Confirm the regulatory category that matches who the other person was and the care that was needed.");
    }
  } else if (!doc.facility_record_confirmed) {
    errors.push("Confirm this stays a facility record. It is not sent to licensing.");
  }
  if (doc.suspected_abuse === "unsure" && doc.bc_category === "not_reportable") {
    errors.push("Unsure is reportable. Talk to the manager and choose the category.");
  }
  if (doc.aga_reported === "yes") {
    if (!doc.aga_reported_at || !filled(doc.aga_reference)) {
      errors.push("If you reported to a designated agency, record the date and a reference. Do not record the reporter's name.");
    }
  }

  if (template === "BC_AL" && suggestion.reportable && !doc.event_time) {
    errors.push("Record the event time. The 24-hour Assisted Living Registry deadline runs from that time.");
  }

  bcDeadlines(incident, doc)
    .filter((row) => row.required)
    .forEach((row) => {
      if (!notificationComplete(incident, row.party)) {
        errors.push(`Record the notification to ${row.role}, including full name, title, time, method, and response.`);
      }
    });

  return errors;
}

module.exports = {
  PRODUCT_BEHAVIOUR,
  DEFAULT_GRACE_MINUTES,
  BC_DESIGNATIONS,
  CATEGORIES,
  incidentDocsBcEnabled,
  allowedProvinces,
  provinceErrorMessage,
  behaviourType,
  productLabel,
  jurisdictionFromSite,
  bcTemplateFromJurisdiction,
  bcTemplate,
  normalizeJurisdiction,
  residentNameError,
  stripReporterIdentity,
  residentInjured,
  bcCategorySuggest,
  bcReportable,
  categoryFits,
  filingCard,
  bcDeadlines,
  notificationComplete,
  validateBc
};
module.exports.default = module.exports;
